import * as React from "react";
import { X, Image, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Capacitor } from "@capacitor/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@mebi/ui";
import { getR2PublicUrl } from "@/storage/uploadService";
import {
  createLocalImagePreviewWithFallback,
  revokeObjectUrl,
} from "@/lib/localImagePreview";

export interface FileInfo {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  previewUrl?: string;
}

export interface FileUploadProps {
  /**
   * Variant of the upload component
   */
  variant?: "medium" | "large";

  /**
   * Enable multiple file selection
   */
  multiple?: boolean;

  /**
   * Maximum number of files (only for multiple mode)
   */
  maxFiles?: number;

  /**
   * Function to handle when file is selected (single mode)
   */
  onFileChange?: (
    file: File,
    previewUrl?: string,
    errorMessage?: string,
  ) => void;

  /**
   * Function to handle when files are selected (multiple mode)
   */
  onFilesChange?: (files: File[]) => void;

  /**
   * Function called when removing file (single mode)
   */
  onFileRemove?: () => void;

  /**
   * Function called when removing file (multiple mode)
   */
  onFileRemoveMultiple?: (fileId: string) => void;

  /**
   * Whether the upload is disabled
   */
  disabled?: boolean;

  /**
   * Class name to be applied to the outer container
   */
  className?: string;

  /**
   * Additional title text to display
   */
  title?: string;

  /**
   * Maximum file size in MB
   */
  maxSizeMB?: number;

  /**
   * Upload in progress
   */
  isUploading?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * Current file info (single mode)
   */
  fileInfo?: {
    name: string;
    size: number;
    type: string;
    /** Optional preview URL for displaying a thumbnail in single mode */
    previewUrl?: string;
  };

  /**
   * Optional preview URL for single mode (e.g., existing image when editing)
   */
  previewUrl?: string;

  /**
   * Current files info (multiple mode)
   */
  files?: FileInfo[];

  /**
   * Accepted file types
   */
  acceptedTypes?: string[];
}

/**
 * FileUpload component - Upload files theo MEBI Design System
 *
 * @example
 * ```tsx
 * <FileUpload
 *   variant="medium"
 *   title="Tải lên tệp"
 *   onFileChange={handleFileChange}
 *   maxSizeMB={10}
 * />
 * ```
 */
const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      variant = "medium",
      multiple = false,
      maxFiles = 5,
      onFileChange,
      onFilesChange,
      onFileRemove,
      onFileRemoveMultiple,
      disabled = false,
      className,
      title,
      maxSizeMB = 10,
      isUploading = false,
      // isSuccess not currently used
      errorMessage,
      fileInfo,
      files = [],
      acceptedTypes = ["image/jpeg", "image/png"],
      previewUrl,
    },
    ref,
  ) => {
    const [isDragOver, setIsDragOver] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [showPreview, setShowPreview] = React.useState(false);
    const [zoom, setZoom] = React.useState(1);
    const [previewItem, setPreviewItem] = React.useState<FileInfo | null>(null);
    const [localErrorMessage, setLocalErrorMessage] = React.useState<
      string | undefined
    >(undefined);
    const fileInputId = React.useId().replaceAll(":", "");
    const [singlePreviewOverrideUrl, setSinglePreviewOverrideUrl] =
      React.useState<string>("");
    const singlePreviewFallbackRef = React.useRef<{
      primary: string;
      fallback: string;
    } | null>(null);

    React.useEffect(() => {
      return () => {
        const currentFallback = singlePreviewFallbackRef.current;
        if (currentFallback) {
          revokeObjectUrl(currentFallback.fallback);
        }
      };
    }, []);

    const validateFile = (file: File): string | null => {
      // Check file type
      const isValidType = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        if (type.includes("/*")) {
          const baseType = type.split("/")[0];
          return file.type.startsWith(baseType);
        }
        return file.type === type;
      });

      if (!isValidType) {
        return `Chỉ hỗ trợ định dạng: ${acceptedTypes.join(", ")}`;
      }

      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        return `Kích thước file không được vượt quá ${maxSizeMB}MB`;
      }

      return null;
    };

    const handleMultipleFiles = (fileList: FileList | File[]) => {
      const fileArray = Array.from(fileList);
      const remainingSlots = maxFiles - files.length;
      if (remainingSlots <= 0) {
        setLocalErrorMessage(`Đã đạt số lượng tệp tối đa (${maxFiles})`);
        return;
      }
      const filesToProcess = fileArray.slice(0, remainingSlots);

      const validFiles: File[] = [];
      const invalidMessages: string[] = [];
      const isOverLimit = fileArray.length > remainingSlots;
      for (const file of filesToProcess) {
        const error = validateFile(file);
        if (error) {
          invalidMessages.push(error);
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length > 0) {
        onFilesChange?.(validFiles);
      }
      if (invalidMessages.length > 0) {
        setLocalErrorMessage(invalidMessages[0]);
        return;
      }
      if (isOverLimit) {
        setLocalErrorMessage(
          `Chỉ có thể thêm tối đa ${remainingSlots} tệp nữa`,
        );
        return;
      }
      setLocalErrorMessage(undefined);
    };

    const handleSingleFile = async (file: File) => {
      const error = validateFile(file);

      if (error) {
        onFileChange?.(file, undefined, error);
        return;
      }

      try {
        const previousFallback = singlePreviewFallbackRef.current;
        if (previousFallback) {
          revokeObjectUrl(previousFallback.fallback);
          singlePreviewFallbackRef.current = null;
        }

        const { previewUrl, fallbackPreviewUrl } =
          await createLocalImagePreviewWithFallback(file);

        if (fallbackPreviewUrl && fallbackPreviewUrl !== previewUrl) {
          singlePreviewFallbackRef.current = {
            primary: previewUrl,
            fallback: fallbackPreviewUrl,
          };
        }

        setSinglePreviewOverrideUrl("");
        onFileChange?.(file, previewUrl);
        setLocalErrorMessage(undefined);
      } catch {
        const previewError =
          "Không thể tạo xem trước tệp. Vui lòng thử lại tệp khác.";
        onFileChange?.(file, undefined, previewError);
        setLocalErrorMessage(previewError);
      }
    };

    const handleFileSelectMultiple = (selectedFiles: FileList | File) => {
      const fileList =
        selectedFiles instanceof FileList ? selectedFiles : [selectedFiles];
      handleMultipleFiles(fileList);
    };

    const handleFileSelectSingle = (selectedFiles: FileList | File) => {
      const file =
        selectedFiles instanceof FileList ? selectedFiles[0] : selectedFiles;
      if (file) {
        void handleSingleFile(file);
      }
    };

    const handleFileSelect = multiple
      ? handleFileSelectMultiple
      : handleFileSelectSingle;

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) {
        handleFileSelect(files);
      }
      // Reset input
      e.target.value = "";
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !isUploading) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled || isUploading) return;

      const files = e.dataTransfer.files;
      if (files) {
        handleFileSelect(files);
      }
    };

    const handleRemove = () => {
      const currentFallback = singlePreviewFallbackRef.current;
      if (currentFallback) {
        revokeObjectUrl(currentFallback.fallback);
        singlePreviewFallbackRef.current = null;
      }

      setSinglePreviewOverrideUrl("");
      onFileRemove?.();
    };

    const handleRemoveMultiple = (fileId: string) => {
      onFileRemoveMultiple?.(fileId);
    };

    const getFileIcon = (fileType: string) => {
      if (fileType.startsWith("image/")) {
        return <Image className="w-4 h-4" />;
      }
      return <FileText className="w-4 h-4" />;
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return (
        Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
      );
    };
    const formatAcceptedTypes = (types: string[]) => {
      const mapped = types.map((type) => {
        const lower = type.toLowerCase();
        if (lower === "image/jpeg" || lower === ".jpeg" || lower === ".jpg")
          return "JPEG";
        if (lower === "image/png" || lower === ".png") return "PNG";
        if (lower === "image/webp" || lower === ".webp") return "WEBP";
        if (lower === "application/pdf" || lower === ".pdf") return "PDF";
        if (lower.startsWith(".")) return lower.replace(".", "").toUpperCase();
        if (lower.startsWith("image/"))
          return lower.split("/")[1]?.toUpperCase() || lower;
        return lower.toUpperCase();
      });
      return mapped.join(", ");
    };

    const isLarge = variant === "large";
    const hasFile = multiple ? files.length > 0 : !!fileInfo || !!previewUrl;
    const canAddMore = multiple ? files.length < maxFiles : true; // Always allow adding for single mode
    const resolveDisplayUrl = (url?: string): string => {
      if (!url) {
        return "";
      }

      const normalized = url.trim();
      if (!normalized) {
        return "";
      }

      if (/^(https?:\/\/|blob:|data:)/i.test(normalized)) {
        return normalized;
      }

      if (/^(file:\/\/|content:\/\/|capacitor:\/\/)/i.test(normalized)) {
        try {
          return Capacitor.convertFileSrc(normalized);
        } catch {
          return normalized;
        }
      }

      if (normalized.startsWith("/assets/")) {
        return normalized;
      }

      return getR2PublicUrl(normalized) || normalized;
    };

    const singlePreviewUrl = resolveDisplayUrl(
      fileInfo?.previewUrl || previewUrl,
    );
    const resolvedSinglePreviewUrl =
      singlePreviewOverrideUrl || singlePreviewUrl;

    React.useEffect(() => {
      setSinglePreviewOverrideUrl("");
    }, [singlePreviewUrl]);

    const handleSinglePreviewError = () => {
      const fallbackMapping = singlePreviewFallbackRef.current;
      if (!fallbackMapping) {
        return;
      }

      if (resolvedSinglePreviewUrl === fallbackMapping.fallback) {
        return;
      }

      const normalizedCurrent = resolvedSinglePreviewUrl.trim();
      const normalizedPrimary = fallbackMapping.primary.trim();
      if (!normalizedCurrent || normalizedCurrent !== normalizedPrimary) {
        return;
      }

      setSinglePreviewOverrideUrl(fallbackMapping.fallback);
    };
    const singleFileType = fileInfo?.type || "";
    const getFileNameFromUrl = (url?: string): string => {
      if (!url) return "Xem trước";
      try {
        const u = new URL(url);
        const last = u.pathname.split("/").pop();
        return decodeURIComponent((last || "").split("?")[0]) || "Xem trước";
      } catch {
        return decodeURIComponent(
          url.split("?")[0].split("/").pop() || "Xem trước",
        );
      }
    };
    const displayName =
      fileInfo?.name ||
      (resolvedSinglePreviewUrl
        ? getFileNameFromUrl(resolvedSinglePreviewUrl)
        : "File.png");
    const displaySize =
      typeof fileInfo?.size === "number" ? formatFileSize(fileInfo.size) : "—";
    const displayErrorMessage = errorMessage || (multiple && localErrorMessage);
    const resolvePreviewUrl = (file?: { previewUrl?: string; url?: string }) =>
      resolveDisplayUrl(file?.previewUrl || file?.url);
    const activePreviewUrl = previewItem
      ? resolvePreviewUrl(previewItem)
      : resolvedSinglePreviewUrl;
    const activePreviewType = previewItem?.type || singleFileType;
    const activePreviewName = previewItem?.name || displayName;
    const isImageFile = (fileType: string, url?: string) => {
      if (fileType.startsWith("image/")) return true;
      if (!url) return false;
      return /\.(png|jpe?g|webp|gif|svg|jfif)$/i.test(url);
    };
    const isPreviewImage = (() => {
      if (!activePreviewUrl) return false;
      if (isImageFile(activePreviewType, activePreviewUrl)) return true;
      // Fallback detection by extension (include svg)
      return /\.(png|jpe?g|webp|gif|svg|jfif)$/i.test(activePreviewUrl);
    })();
    const isDoc = (() => {
      if (!activePreviewUrl) return false;
      if (
        activePreviewType &&
        (activePreviewType === "application/msword" ||
          activePreviewType ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      )
        return true;
      return /\.(docx?|application\/msword|officedocument\.wordprocessingml\.document)$/i.test(
        activePreviewUrl,
      );
    })();
    const isPdf = (() => {
      if (!activePreviewUrl) return false;
      if (activePreviewType && activePreviewType === "application/pdf")
        return true;
      return /\.(pdf)$/i.test(activePreviewUrl);
    })();

    const handleZoomWheel = (e: React.WheelEvent) => {
      // Prevent parent scroll when zooming
      e.stopPropagation();
      const delta = e.deltaY;
      setZoom((prev) => {
        const next =
          delta > 0 ? Math.max(1, prev - 0.1) : Math.min(5, prev + 0.1);
        return Number(next.toFixed(2));
      });
    };

    const renderDocumentPreview = () => {
      if (isPdf) {
        return (
          <object
            data={activePreviewUrl}
            type="application/pdf"
            className="w-full h-full"
          >
            <p className="p-4 text-sm">
              Không thể hiển thị PDF.{" "}
              <a
                href={activePreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Mở trong tab mới
              </a>
            </p>
          </object>
        );
      }

      if (isDoc) {
        return (
          <div className="w-full h-full flex items-center justify-center p-4">
            <div className="text-center">
              <p className="text-sm mb-3">
                Tệp DOC/DOCX không thể xem trực tiếp trong trình duyệt.
              </p>
              <div className="flex items-center justify-center gap-3">
                <a
                  href={activePreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-mebi-yellow text-black rounded-md border border-neutral-300"
                >
                  Mở trong tab mới
                </a>
                <a
                  href={activePreviewUrl}
                  download
                  className="px-3 py-2 bg-neutral-200 text-black rounded-md border border-neutral-300"
                >
                  Tải xuống
                </a>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="w-full h-full flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-sm mb-3">Không thể xem trước loại tệp này.</p>
            <a
              href={activePreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 bg-mebi-yellow text-black rounded-md border border-neutral-300"
            >
              Mở trong tab mới
            </a>
          </div>
        </div>
      );
    };

    const renderPreviewContent = () => {
      if (isPreviewImage) {
        return (
          <div
            className="w-full h-[70vh] flex items-center justify-center bg-neutral-50 overflow-hidden"
            onWheel={handleZoomWheel}
          >
            <img
              src={activePreviewUrl}
              alt={activePreviewName}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
              }}
              onError={handleSinglePreviewError}
              onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
            />
            {/* Zoom controls */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 border border-neutral-300 rounded-md px-2 py-1 shadow-sm">
              <Button
                variant="ghost"
                size="sm"
                className="px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoom((z) => Math.max(0.5, Number((z - 0.1).toFixed(2))));
                }}
              >
                −
              </Button>
              <input
                type="range"
                min={0.5}
                max={5}
                step={0.1}
                value={zoom}
                onChange={(e) =>
                  setZoom(Number(Number(e.target.value).toFixed(2)))
                }
              />
              <Button
                variant="ghost"
                size="sm"
                className="px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoom((z) => Math.min(5, Number((z + 0.1).toFixed(2))));
                }}
              >
                +
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoom(1);
                }}
              >
                Fit
              </Button>
            </div>
          </div>
        );
      }

      return <div className="w-full h-[70vh]">{renderDocumentPreview()}</div>;
    };

    return (
      <div ref={ref} className={cn("relative w-full", className)}>
        {/* Title */}
        {title && (
          <div className="mb-3">
            <p className="text-sm font-medium text-black">{title}</p>
          </div>
        )}

        {/* Upload container - Always show unless disabled */}
        {canAddMore && !disabled && (
          <label
            htmlFor={fileInputId}
            className={cn(
              "relative rounded-lg border border-dashed border-Color-Stroke-brand flex w-full justify-start items-center gap-2 transition-colors cursor-pointer text-left bg-transparent",
              {
                "p-4": !isLarge,
                "p-4 flex-col": isLarge,
                "bg-Color-Background-neutral-hover": isDragOver,
              },
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              id={fileInputId}
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes.join(",")}
              onChange={handleFileInputChange}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              disabled={disabled || isUploading}
              multiple={multiple}
            />

            {/* Image icon - responsive sizing */}
            <div
              className={cn("flex-shrink-0 overflow-hidden", {
                "w-9 h-9": !isLarge,
                "w-12 h-12": isLarge,
              })}
            >
              <svg
                className={cn("text-Color-Icon-default w-full h-full")}
                viewBox="0 0 37 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.5 36C3.4 36 2.45833 35.6083 1.675 34.825C0.891667 34.0417 0.5 33.1 0.5 32V20H4.5V32H16.5V36H4.5ZM20.5 36V32H32.5V20H36.5V32C36.5 33.1 36.1083 34.0417 35.325 34.825C34.5417 35.6083 33.6 36 32.5 36H20.5ZM6.5 28L12.5 20L17 26L23 18L30.5 28H6.5ZM0.5 16V4C0.5 2.9 0.891667 1.95833 1.675 1.175C2.45833 0.391667 3.4 0 4.5 0H16.5V4H4.5V16H0.5ZM32.5 16V4H20.5V0H32.5C33.6 0 34.5417 0.391667 35.325 1.175C36.1083 1.95833 36.5 2.9 36.5 4V16H32.5ZM25.5 14C24.6333 14 23.9167 13.7167 23.35 13.15C22.7833 12.5833 22.5 11.8667 22.5 11C22.5 10.1333 22.7833 9.41667 23.35 8.85C23.9167 8.28333 24.6333 8 25.5 8C26.3667 8 27.0833 8.28333 27.65 8.85C28.2167 9.41667 28.5 10.1333 28.5 11C28.5 11.8667 28.2167 12.5833 27.65 13.15C27.0833 13.7167 26.3667 14 25.5 14Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            {/* Text content - responsive layout */}
            <div
              className={cn("flex flex-col min-w-0", {
                "flex-1 items-start": !isLarge,
                "items-center text-center": isLarge,
              })}
            >
              <div className="inline-flex justify-start items-center gap-1">
                <span className="text-Color-Text-neutral-black text-sm font-normal font-['Be_Vietnam_Pro'] leading-5">
                  {multiple ? "Chọn tệp" : "Thêm tệp"}
                </span>
                <span className="text-Color-Text-brand-default text-sm font-semibold font-['Be_Vietnam_Pro'] underline leading-5">
                  tại đây
                </span>
              </div>
              <div className="text-Color-Text-neutral-supplement text-[10px] font-light font-['Be_Vietnam_Pro'] leading-4 mt-1">
                {formatAcceptedTypes(acceptedTypes)}
                {maxSizeMB ? ` (kích thước tối đa ${maxSizeMB}MB)` : ""}
              </div>
            </div>
          </label>
        )}

        {/* Single file display - responsive layout */}
        {!multiple && hasFile && (
          <div className="mt-3">
            {isUploading && (
              <div className="flex items-center justify-between w-full border border-neutral-400 rounded-lg p-2 sm:p-[7px_15px] min-h-[48px] sm:h-[54px]">
                <div className="flex flex-grow items-start gap-2 sm:gap-[11px] min-w-0">
                  <div className="flex-shrink-0 w-4 h-4 sm:w-6 sm:h-6 overflow-hidden">
                    <Image className="w-full h-full text-gray-600" />
                  </div>
                  <div className="flex flex-col flex-grow items-start min-w-0">
                    <p className="text-xs sm:text-sm leading-[18px] sm:leading-[22px] text-black font-['Be_Vietnam_Pro'] truncate w-full">
                      {fileInfo?.name || "File.png"}
                    </p>
                    <p className="text-[9px] sm:text-[10px] leading-3 sm:leading-4 font-light text-neutral-600 font-['Be_Vietnam_Pro']">
                      {formatFileSize(fileInfo?.size || 0) || "1MB"}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-4 h-4 overflow-hidden ml-2">
                  <div className="animate-spin rounded-full w-full h-full border-2 border-gray-300 border-t-blue-600"></div>
                </div>
              </div>
            )}

            {!isUploading && !errorMessage && (
              <div className="flex items-center justify-between w-full border border-neutral-400 rounded-lg p-2 sm:p-[7px_15px] min-h-[60px] sm:min-h-[70px]">
                <div className="flex flex-grow items-center gap-2 sm:gap-[11px] min-w-0">
                  {/* Thumbnail or icon */}
                  {resolvedSinglePreviewUrl ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewItem(null);
                        setShowPreview(true);
                      }}
                      className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-md overflow-hidden bg-neutral-100"
                    >
                      {isPreviewImage ? (
                        <img
                          src={resolvedSinglePreviewUrl}
                          alt={fileInfo?.name || "Preview"}
                          className="w-full h-full object-contain"
                          onError={handleSinglePreviewError}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {getFileIcon(singleFileType)}
                        </div>
                      )}
                    </button>
                  ) : (
                    <div className="flex-shrink-0 w-4 h-4 sm:w-6 sm:h-6 overflow-hidden">
                      {getFileIcon(fileInfo?.type || "")}
                    </div>
                  )}

                  {/* File info */}
                  <div className="flex flex-col flex-grow items-start min-w-0">
                    <p className="text-xs sm:text-sm leading-[18px] sm:leading-[22px] text-black font-['Be_Vietnam_Pro'] truncate w-full">
                      {displayName}
                    </p>
                    <p className="text-[9px] sm:text-[10px] leading-3 sm:leading-4 font-light text-neutral-600 font-['Be_Vietnam_Pro']">
                      {displaySize}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  className="flex-shrink-0 w-4 h-4 p-0 hover:bg-red-100 ml-2"
                >
                  <X className="w-full h-full text-red-600" />
                </Button>
              </div>
            )}

            {errorMessage && (
              <div className="flex items-center justify-between w-full border border-mebi-red rounded-lg p-2 sm:p-[7px_15px] min-h-[48px] sm:h-[54px]">
                <div className="flex flex-grow items-start gap-2 sm:gap-[11px] min-w-0">
                  <div className="flex-shrink-0 w-4 h-4 sm:w-6 sm:h-6 overflow-hidden">
                    <Image className="w-full h-full text-red-600" />
                  </div>
                  <div className="flex flex-col flex-grow items-start min-w-0">
                    <p className="text-xs sm:text-sm leading-[18px] sm:leading-[22px] text-black font-['Be_Vietnam_Pro'] truncate w-full">
                      {fileInfo?.name || displayName}
                    </p>
                    <p className="text-[9px] sm:text-[10px] leading-3 sm:leading-4 font-light text-mebi-red font-['Be_Vietnam_Pro']">
                      {errorMessage ||
                        "Đã xảy ra lỗi. Hãy xóa tệp này và thử lại"}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  className="flex-shrink-0 w-4 h-4 p-0 hover:bg-red-100 ml-2"
                >
                  <X className="w-full h-full text-red-600" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Multiple files list - responsive design */}
        {multiple && files.length > 0 && (
          <div className="space-y-2 mt-3">
            {files.map((file) => {
              const filePreviewUrl = resolvePreviewUrl(file);
              const fileHasPreview = Boolean(filePreviewUrl);
              const fileIsImage = isImageFile(file.type, filePreviewUrl);

              return (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-grow">
                    {fileHasPreview ? (
                      <button
                        type="button"
                        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-md overflow-hidden bg-neutral-100"
                        onClick={() => {
                          setPreviewItem(file);
                          setShowPreview(true);
                        }}
                      >
                        {fileIsImage ? (
                          <img
                            src={filePreviewUrl}
                            alt={file.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {getFileIcon(file.type)}
                          </div>
                        )}
                      </button>
                    ) : (
                      getFileIcon(file.type)
                    )}
                    <div className="flex-1 min-w-0">
                      {fileHasPreview ? (
                        <button
                          type="button"
                          className="text-xs sm:text-sm font-medium text-gray-900 truncate w-full text-left hover:text-blue-600"
                          onClick={() => {
                            setPreviewItem(file);
                            setShowPreview(true);
                          }}
                        >
                          {file.name}
                        </button>
                      ) : (
                        <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </div>
                      )}
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMultiple(file.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                    disabled={disabled}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Error message */}
        {displayErrorMessage && (
          <p className="mt-2 text-xs text-red-600">{displayErrorMessage}</p>
        )}

        {/* Hidden file input */}
        {/* Large preview dialog */}
        {activePreviewUrl && (
          <Dialog
            open={showPreview}
            onOpenChange={(open) => {
              setShowPreview(open);
              if (!open) {
                setPreviewItem(null);
                setZoom(1);
              }
            }}
          >
            <DialogContent className="bg-white p-0 border border-neutral-400 rounded-xl max-w-[95vw] w-[95vw] sm:max-w-[720px]">
              <DialogHeader className="px-4 pt-4">
                <DialogTitle>Xem trước tệp</DialogTitle>
              </DialogHeader>
              {renderPreviewContent()}
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  },
);

FileUpload.displayName = "FileUpload";

export { FileUpload };
